<script setup lang="ts">
export interface SocialLinks {
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  mailUrl?: string;
  googlePlayUrl?: string;
  appStoreUrl?: string;
}

export interface BaseFooterProps {
  /**
   * An array of the footer links that placed next to the logo.
   */
  links?: FooterLink[];
  /**
   * An object of social media links in footer.
   */
  socialLinks?: SocialLinks;
  /**
   * URL to the footer logo. Height is fixed to 56px, width auto adjusted.
   */
  logoImageUrl?: string;
  /**
   * Shows only line with rights text in the footer.
   */
  rightsOnly?: boolean;
  /**
   * The name of the customer to be shown in the rights text of footer.
   */
  customerName?: string;
  isThemeSwitcherShown?: boolean;
}

const { t } = useI18n();
const color = useColorMode();
const props = withDefaults(defineProps<BaseFooterProps>(), {
  customerName: "Pixellot",
  logoImageUrl: "/images/mock/logo.svg",
  isThemeSwitcherShown: true,
});
const links = computed(() => props.links?.map((link) => {
  const i18nKey = link.type === "custom" ? t("footer_links.custom", { custom: link.title }) : t(`footer_links.${link.type}`);
  return {
    url: link.url,
    title: link.title,
    i18nKey,
  };
}));
const currentYear = computed(() => new Date().getFullYear());
const activeTheme = computed(() => (props.isThemeSwitcherShown ? color.value === "dark" ? "light" : "dark" : "light"));
</script>

<template>
  <footer class="baseFooter">
    <div v-if="!props.rightsOnly" class="flex flex-wrap items-center justify-center py-10 md:flex-nowrap md:justify-between">
      <div class="flex items-center font-semibold">
        <BaseImage
          class="baseFooter__logo"
          :src="props.logoImageUrl"
          fallback-src="/images/mock/logo.svg"
          :alt="`${props.customerName} logo`"
          width="auto"
          :height="56"
        />

        <div v-if="links?.length" class="flex flex-wrap items-center">
          <BaseButton
            v-for="link in links"
            :key="link.url"
            class="text-sm md:text-base mx-2 sm:mx-3 lg:mx-4"
            :tag="link.url.includes('http') ? 'a' : 'nuxt-link'"
            :to="link.url.includes('http') ? {} : link.url"
            :href="link.url.includes('http') ? link.url : undefined"
            type="secondary"
            :name="link.title"
            link
          >
            {{ link.i18nKey }}
          </BaseButton>
        </div>
      </div>
      <div v-if="props.socialLinks" class="flex flex-wrap items-center justify-center">
        <div class="flex justify-center flex-1 my-4 md:my-2">
          <a
            v-if="props.socialLinks.facebookUrl"
            :href="props.socialLinks.facebookUrl"
            target="_blank"
            class="mx-4"
            aria-label="Facebook Link"
          >
            <IconSocialFacebook />
          </a>
          <a
            v-if="props.socialLinks.twitterUrl"
            :href="props.socialLinks.twitterUrl"
            target="_blank"
            class="mx-4"
            aria-label="Twitter Link"
          >
            <IconSocialTwitter />
          </a>
          <a
            v-if="props.socialLinks.instagramUrl"
            :href="props.socialLinks.instagramUrl"
            target="_blank"
            class="mx-4"
            aria-label="Instagram Link"
          >
            <IconSocialInstagram />
          </a>
          <a v-if="props.socialLinks.mailUrl" :href="`mailto:${props.socialLinks.mailUrl}`" class="mx-4" aria-label="Mail Link">
            <IconSocialMail />
          </a>
        </div>
        <div class="flex flex-wrap items-center justify-center my-5 gap-3">
          <a
            v-if="props.socialLinks.googlePlayUrl"
            :href="props.socialLinks.googlePlayUrl"
            target="_blank"
            aria-label="Google Play"
          >
            <IconSocialGooglePlay />
          </a>
          <a
            v-if="props.socialLinks.appStoreUrl"
            :href="props.socialLinks.appStoreUrl"
            target="_blank"
            aria-label="App Store"
          >
            <IconSocialAppStore />
          </a>
        </div>
        <div v-if="props.isThemeSwitcherShown" class="flex mx-4">
          <BaseThemeSwitcher />
        </div>
      </div>
    </div>

    <div class="baseFooter__rights">
      <span> © {{ currentYear }} {{ props.customerName }}. {{ t('baseFooter.rights') }}</span>

      <div class="baseFooter__logo">
        <span class="flex-shrink-0 text-main">{{ t('labels.powered_by') }}</span>
        <img class="mx-2 h-2.5" :src="`/images/base/pixellot-logo-${activeTheme}.png`" height="10" width="64" alt="Pixellot-logo">
      </div>
    </div>
  </footer>
</template>

<style lang="scss">
.baseFooter {
  @apply w-full px-10;

  background-color: var(--base-footer-backround-color);

  .baseFooter__rights {
    @apply flex items-center py-6
    md:justify-between
    sm:flex flex-wrap justify-center;
    font-size: 11px;
    font-weight: 400;
    line-height: 13px;
    color: var(--base-secondary-color);

    &:last-child {
      border-top: 1px solid var(--base-footer-line-color);
    }
    .baseFooter__logo {
      @apply italic mx-2 font-semibold h-12
      text-xs text-white flex items-center;
    }
  }

  .baseBtn.baseBtn-link {
    color: var(--base-text-color);
  }

  .router-link-active {
    color: var(--base-text-color) !important;
  }
}

.baseFooter__logo {
  //TODO: change style
  @apply max-h-14 w-auto flex-shrink-0;
}
</style>
